/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { getBaseUrl } from '@pp/axios'
import { compact } from 'lodash'
import { ACCEPTED_FILES, MAX_FILE_SIZE } from 'Src/constants/Global'
import { Form, Input } from 'antd'
import { BtnComponent, IconComponent, TooltipLabelComponent, i18n, UISizes, UITypes } from 'LibNeo'
import { GetFieldDecoratorOptions, WrappedFormUtils } from 'antd/lib/form/Form'
import { ReceivingFileModel } from 'Models'
import { useAppDispatch } from 'Src/hooks'
import { useSelector } from 'react-redux'
import { getActualSize, XOR, filesLabel } from 'Helpers'
import { FieldNames } from 'Constants/FieldNames'
import { useUnmount } from 'Src/hooks/useUnmount'
import { Callback } from 'Src/types/utilTypes'
import { fileSlice, fileSelectors } from 'Modules'
import { UploadPreviewComponent } from './UploadPreview.component'
import {
	ButtonZone,
	ButtonZoneWrapper,
	DropzoneArea,
	DropzoneStyled,
	DropzoneSubtitle,
	DropzoneTitle,
	FileError,
} from './styled'

export interface FileWithPreview extends ReceivingFileModel {
	preview?: string
	error?: string
}

enum DragActions {
	ENTER = 'enter',
	LEAVE = 'leave',
}

const uploadIcons = {
	upload: 'file-upload',
	refresh: 'refresh',
	lock: 'lock',
}

type UploadProps = {
	fieldName?: FieldNames | string
	acceptedFiles?: string // список расширений файлов с точкой, строкой через запятую
	maxFileSize?: number
	maxFilesCount?: number
	multiple?: boolean // множественное добавление файлов, обязательно, если maxFilesCount > 1
	btnTitle?: string
	label?: string
	minResolution?: string
	maxResolution?: string
	tooltip?: string
	className?: string
	disabled?: boolean
	required?: boolean // для отображения звездочки обязательного заполнения
	loading?: boolean // для отображения иконки загрузки
	children?: JSX.Element
	initialValue?: ReceivingFileModel | ReceivingFileModel[]
	errorText?: string
	isDragAndDrop?: boolean
	fixedFileItemWidth?: boolean
	isHideDropzone?: boolean
	widthValue?: number | string
	heightValue?: number | string
	clearAfterActionCreate?: boolean
	isViewDeleteIcon?: boolean
	noAutoUpload?: boolean
	onAutoUpload?: Callback
	fullWidthButton?: boolean
} & XOR<
	{
		// eslint-disable-next-line max-len
		actionCreate?: (fieldName: FieldNames, files: FileWithPreview | FileWithPreview[]) => void // если maxFilesCount === 1 тогда один файл, иначе массив
		actionUpdate?: (fieldName: FieldNames, files: FileWithPreview | FileWithPreview[]) => void
		actionDelete?: (fieldName: FieldNames, files?: FileWithPreview | FileWithPreview[]) => void
	},
	{
		form?: WrappedFormUtils
		fieldOptions?: GetFieldDecoratorOptions
	}
>

export const UploadComponent: FC<UploadProps> = ({
	actionCreate,
	actionUpdate,
	actionDelete,
	form,
	fieldName,
	fieldOptions,
	btnTitle,
	label,
	tooltip,
	acceptedFiles = ACCEPTED_FILES,
	children,
	minResolution,
	maxResolution,
	maxFileSize = MAX_FILE_SIZE,
	multiple = false,
	maxFilesCount = 1,
	className,
	fullWidthButton,
	disabled,
	loading,
	initialValue,
	errorText,
	required,
	isDragAndDrop = false,
	fixedFileItemWidth = true,
	isHideDropzone = false,
	widthValue = 'auto',
	heightValue = 280,
	clearAfterActionCreate = false,
	isViewDeleteIcon = false,
	noAutoUpload = false,
	onAutoUpload,
}) => {
	const [fileList, setFileList] = useState<FileWithPreview[]>([])
	const [error, setError] = useState<string | null>(null)
	const dropzoneRef = useRef(null)
	const { getFile } = useAppDispatch({
		getFile: fileSlice.actions.getFileBlob,
	})
	const files = useSelector(fileSelectors.getFilesBlob)

	const btnTooltip = useRef(
		`До ${getActualSize(maxFileSize)}\n${acceptedFiles}${
			minResolution ? `\nМинимальное разрешение ${minResolution}` : ''
		}${maxResolution ? `\nМаксимальное разрешение ${maxResolution}` : ''}`
	)

	const btnText = useRef(btnTitle)
	const btnIcon = useRef(uploadIcons.upload)
	const [dragEnter, setDragEnter] = useState(false)

	const getResultFile = (fileList: FileWithPreview[]) => {
		return maxFilesCount === 1 ? fileList[0] : fileList
	}

	const getPrepareFile = (prepareFile) => {
		let file = prepareFile
		const url = file.file
		if (!noAutoUpload) {
			const blobFile = url?.startsWith('public') ? `${getBaseUrl(true)}/${url}` : files.get(url)
			if (blobFile) {
				file = {
					...file,
					preview: url?.startsWith('public') ? blobFile : window.URL.createObjectURL(blobFile),
				}
			} else {
				getFile({
					url: prepareFile.file,
					hideDefaultLoader: Boolean(onAutoUpload),
					callback: onAutoUpload,
				})
			}
		}
		return file
	}

	const setInitialValue = () => {
		const initialFile = fieldOptions?.initialValue || initialValue
		const isArray = Array.isArray(initialFile)
		const prepareFiles = []
		let prepareFile = null
		if (isArray) {
			initialFile.forEach((current) => {
				prepareFile = getPrepareFile(current)
				prepareFiles.push(prepareFile)
			})
		} else {
			prepareFile = getPrepareFile(initialFile)
			prepareFiles.push(prepareFile)
		}
		setFileList(prepareFiles)
	}

	const clearValue = () => {
		setFileList([])
		form?.setFieldsValue({ [fieldName]: undefined })
	}

	useUnmount(() => {
		if (fieldOptions?.initialValue || initialValue) {
			fileList?.forEach((file) => {
				window.URL.revokeObjectURL(file.preview)
			})
		}
	})

	useEffect(() => {
		setError(null)
		if (errorText) {
			setError(errorText)
			setFileList([])
		}
	}, [errorText])

	useEffect(() => {
		setFileList(
			fileList?.map((item): FileWithPreview => {
				if (!item.preview) {
					const url = item.file
					const blobFile = files.get(url)
					if (blobFile) {
						item = { ...item, preview: window.URL.createObjectURL(blobFile) }
					}
				}
				return item
			})
		)
	}, [files.size])

	useEffect(() => {
		if (fieldOptions?.initialValue || initialValue) {
			setInitialValue()
			if (!multiple) {
				btnIcon.current = uploadIcons.refresh
				btnText.current = 'Загрузить другой файл'
			}
		} else {
			clearValue()
		}
	}, [fieldOptions?.initialValue, initialValue])

	useEffect(() => {
		if (fieldName && form && !form.getFieldValue(fieldName)) setFileList([])
	}, [form?.getFieldValue(fieldName)])

	const onDropFiles = useCallback(
		(acceptFiles: ReceivingFileModel[], rejectFiles?: ReceivingFileModel[]) => {
			let notUnique = false
			setError(null)

			if (rejectFiles?.length && !children) {
				if (acceptFiles?.length) {
					setError(i18n('common.some_wrong_file_type'))
				} else {
					setError(i18n('common.wrong_file_type'))
				}

				if (!multiple) {
					clearValue()
					btnIcon.current = uploadIcons.upload
					btnText.current = btnTitle
				}
				return
			}
			const fileListNames = fileList?.map((file) => file.name)
			let list = fileList ? [...fileList] : []

			notUnique = acceptFiles.some((acceptedItem) => fileListNames.includes(acceptedItem.name))
			if (notUnique) {
				setError(i18n('common.file_already_upload'))
				if (!multiple) return
			}

			acceptFiles.forEach((file) => {
				if (!fileListNames?.includes(file.name) && list.length < maxFilesCount) list.push(file)
				else if (!fileListNames?.includes(file.name) && list.length === maxFilesCount && !multiple)
					list = [file]
			})

			if (!multiple && acceptFiles?.length) {
				btnIcon.current = uploadIcons.refresh
				btnText.current = 'Загрузить другой файл'
			}

			if (list?.length >= maxFilesCount && multiple) {
				btnTooltip.current = `Достигнут лимит загрузки: не более ${maxFilesCount} файлов.`
			}

			if (!children) setFileList(list)
			if (form) {
				form.setFieldsValue({ [fieldName]: getResultFile(list) })
			} else if (!fileList.length) {
				actionCreate?.(fieldName as FieldNames, getResultFile(list))
				if (clearAfterActionCreate) clearValue()
			} else {
				actionUpdate?.(fieldName as FieldNames, getResultFile(list))
			}
		},
		[fileList, actionCreate, actionUpdate]
	)

	const onDragAction = useCallback(
		(dragAction: DragActions) => (event: React.DragEvent) => {
			const dropzoneNode = dropzoneRef?.current?.node
			if (!dropzoneNode) return
			if (event.target !== dropzoneNode) return
			switch (dragAction) {
				case DragActions.ENTER:
					setDragEnter(true)
					break
				case DragActions.LEAVE:
					setDragEnter(false)
					break
				default:
					setDragEnter(false)
			}
		},
		[]
	)

	const deleteFile = useCallback(
		(index: number) => {
			let list = [...fileList]
			delete list[index]
			list = compact(list)
			setFileList(list)
			if (!multiple && !list?.length) {
				setError(null)
				btnIcon.current = uploadIcons.upload
				btnText.current = btnTitle
			}
			btnTooltip.current = `До ${getActualSize(maxFileSize)}\n${acceptedFiles}`
			if (form) {
				form.setFieldsValue({ [fieldName]: getResultFile(list) })
			} else actionDelete?.(fieldName as FieldNames, getResultFile(list))
		},
		[fileList]
	)

	const getDropzone = () => {
		const disabledDropzone = disabled || (maxFilesCount === fileList.length && !error)

		const renderTitle = () => {
			if (disabledDropzone) return 'Загрузка недоступна'
			return 'Чтобы загрузить,\nнажмите или перетащите файл в эту область'
		}

		const renderSubtitle = () => {
			if (disabledDropzone) {
				if (fileList?.length === 0) return ''
				return `Достигнут лимит загрузки: не более ${maxFilesCount} ${filesLabel(maxFilesCount)}.`
			}
			if (maxFilesCount === 1) {
				return `Один файл • до ${getActualSize(maxFileSize)}\n${acceptedFiles}`
			}
			return `До ${maxFilesCount} файлов • по ${getActualSize(maxFileSize)}\n${acceptedFiles}`
		}

		const isError = () => {
			const errorForm = form?.getFieldError(fieldName)
			if (errorForm) return Boolean(errorForm)
			if (error) return Boolean(error)
		}

		if (children) {
			return (
				<ButtonZoneWrapper>
					<DropzoneStyled
						className={className}
						ref={dropzoneRef}
						multiple={multiple}
						onDrop={onDropFiles}
						accept={acceptedFiles}
						maxSize={maxFileSize}
						disableClick={disabledDropzone}
						disabled={disabled || (multiple && maxFilesCount === fileList.length && !error)}
						hasError={error || form?.getFieldError(fieldName)}
						success={!multiple && fileList.length}
					>
						{children}
					</DropzoneStyled>
					{Boolean(error) && <FileError>{error}</FileError>}
				</ButtonZoneWrapper>
			)
		}

		if (!isDragAndDrop) {
			return (
				<>
					{!isHideDropzone && (
						<ButtonZoneWrapper>
							<ButtonZone
								ref={dropzoneRef}
								onClick={() => {
									if (error) setError(null)
								}}
								multiple={multiple}
								accept={acceptedFiles}
								onDrop={onDropFiles}
								maxSize={maxFileSize}
								disableClick={multiple && disabledDropzone}
							>
								<BtnComponent
									block={fullWidthButton}
									icon={btnIcon.current}
									title={btnText.current}
									tooltip={btnTooltip.current}
									disabled={multiple && (disabledDropzone as boolean)}
									outline
								/>
							</ButtonZone>
							{Boolean(error) && !disabled && <FileError>{error}</FileError>}
						</ButtonZoneWrapper>
					)}

					<UploadPreviewComponent
						fileList={fileList}
						deleteFile={(!isHideDropzone || isViewDeleteIcon) && !disabled && deleteFile}
						fixWidth={fixedFileItemWidth}
						loading={loading}
						noAutoUpload={noAutoUpload}
					/>
				</>
			)
		}

		return (
			<>
				{!isHideDropzone && (
					<>
						{Boolean(label) && (
							<TooltipLabelComponent title={label} required={required} tooltip={tooltip} />
						)}
						<DropzoneArea
							ref={dropzoneRef}
							onClick={() => {
								if (error) setError(null)
							}}
							multiple={multiple}
							accept={acceptedFiles}
							onDrop={onDropFiles}
							onDragEnter={onDragAction(DragActions.ENTER)}
							onDragLeave={onDragAction(DragActions.LEAVE)}
							maxSize={maxFileSize}
							disableClick={disabledDropzone}
							dragEnter={dragEnter}
							$width={widthValue}
							$height={heightValue}
						>
							<IconComponent
								name={disabledDropzone ? uploadIcons.lock : uploadIcons.upload}
								type={disabledDropzone ? UITypes.PASSIVE : UITypes.PRIMARY}
								size={UISizes.SMALL}
							/>
							<DropzoneTitle>{renderTitle()}</DropzoneTitle>
							<DropzoneSubtitle>{renderSubtitle()}</DropzoneSubtitle>
						</DropzoneArea>
					</>
				)}
				{isError() && <FileError>{error || form?.getFieldError(fieldName)}</FileError>}
				<UploadPreviewComponent
					fileList={fileList}
					deleteFile={(!isHideDropzone || isViewDeleteIcon) && deleteFile}
					fixWidth={fixedFileItemWidth}
					loading={loading}
					noAutoUpload={noAutoUpload}
				/>
			</>
		)
	}

	return (
		<>
			{form ? (
				<div className="ppneo-common__input--hidden">
					<Form.Item>
						{form.getFieldDecorator(fieldName, fieldOptions)(<Input />)}
						{getDropzone()}
					</Form.Item>
				</div>
			) : (
				getDropzone()
			)}
		</>
	)
}
